import * as React from 'react'
import { type V2_MetaFunction, type LinksFunction } from '@remix-run/node'
import {
	isRouteErrorResponse,
	useLocation,
	useOutletContext,
	useRouteError,
} from '@remix-run/react'
// import { ScrollingCarousel } from '@trendyol-js/react-carousel'
// import { MoveLeftIcon, MoveRightIcon } from 'lucide-react'
import ImageOverlay from '~/components/image-overlay.tsx'
import { Tabs } from '@mantine/core'
import { AppWindowIcon, InfoIcon, ShieldIcon } from 'lucide-react'
import SupportForm from './resources+/support-form.tsx'
import ApplyForm from './resources+/apply-form.tsx'
import InquiryForm from './resources+/inquiry-form.tsx'
import StudyIcon from '~/components/icon/study.tsx'
import WorkIcon from '~/components/icon/work.tsx'
import PrayIcon from '~/components/icon/pray.tsx'
import { Separator } from '~/components/ui/separator.tsx'
import { type RootOutletContext } from '~/root.tsx'

export const meta: V2_MetaFunction = () => [{ title: 'VIA NOVA' }]
export const links: LinksFunction = () => {
	return []
}

export default function Index() {
	const context = useOutletContext<RootOutletContext>()
	const location = useLocation()

	React.useEffect(() => {
		if (location.state?.scroll) {
			switch (location.state?.scroll) {
				case 'mission': {
					context.missionScroll.scrollIntoView()
					return () => context.missionScroll.cancel()
				}
				case 'story': {
					context.storyScroll.scrollIntoView()

					return () => context.storyScroll.cancel()
				}
				default: {
					break
				}
			}
		}
	}, [context.missionScroll, context.storyScroll, location.state?.scroll])

	return (
		<>
			<section
				id="mission"
				ref={context.missionScroll.targetRef}
				className="relative flex h-[400px] min-h-[33vh] overflow-hidden border-b-2 border-black md:h-[688px] md:min-h-[44vh] md:items-center md:justify-center lg:h-[688px]"
			>
				<ImageOverlay src="/img/via2.png" opacity="0.60" position="50% 0%" />
				<div className="z-20 flex w-full flex-row p-6 text-gray-200 md:absolute md:right-12 md:top-8 md:p-0">
					{/* <h2 className="font-title text-2xl font-normal leading-6 md:text-5xl">
						The pillars of study, work, and prayer.
					</h2> */}
					<div className="hidden md:block md:w-1/2"></div>
					<div className="flex flex-col space-y-4 bg-gray-400  bg-opacity-70 p-6 text-base text-gray-200 sm:text-lg md:space-y-16 md:text-3xl  lg:w-[600px] lg:bg-transparent ">
						<div>
							Via Nova offers a live-in experience of intensive intellectual,
							professional, and spiritual formation tailored to the goals and
							passions of young adults eager to apply themselves to the ancient
							formula of <b>study</b>, <b>work</b>, & <b>prayer</b>.
						</div>
						<div>
							Through seminars, apprenticeships, spiritual regimens, and more,
							Via Nova invites participants to give ten months to developing
							personal habits, acquiring practical knowledge, and ultimately
							enjoying a life of <b>freedom</b>.
						</div>
					</div>
				</div>
			</section>
			<section
				id="story"
				ref={context.storyScroll.targetRef}
				className="border-b-2 border-black bg-background p-6 md:p-36 md:pt-20"
			>
				<div className="mx-auto w-full ">
					<h2 className="mb-10 font-title  text-5xl   text-accent-dark text-gray-900 md:text-4xl md:leading-10 ">
						Our Vison
					</h2>
					<p className="mb-4 text-lg leading-7 text-accent-dark md:text-2xl md:leading-10">
						What Via offers is simple: a life of study, work, and prayer, lived
						out in a community of people who desire largely the same things,
						namely:
					</p>
					<ul className="mb-10 ml-8 grid  grid-cols-1 text-lg leading-7 text-accent-dark md:text-2xl md:leading-10 lg:grid-cols-2">
						<li className="before:pr-2 before:content-['◦']">
							knowledge of oneself, the world, and God
						</li>
						<li className="before:pr-2 before:content-['◦']">
							habits of order aimed at self-mastery
						</li>
						<li className="before:pr-2 before:content-['◦']">
							meaningful work in potential careers
						</li>
						<li className="before:pr-2 before:content-['◦']">
							a deep capacity for prayer and meditation
						</li>
						<li className="before:pr-2 before:content-['◦']">
							an ability to share the faith with confidence
						</li>
						<li className="before:pr-2 before:content-['◦']">
							freedom from vice and attachment
						</li>
						<li className="before:pr-2 before:content-['◦']">
							an abiding love of God and neighbor
						</li>
						<li className="before:pr-2 before:content-['◦']">
							a clear understanding of one’s calling
						</li>
					</ul>
					<Separator className="my-4 bg-gray-800" />
					<div className="flex flex-col gap-4   ">
						<p className="mb-4 text-center text-xl leading-7 text-accent-dark md:text-3xl md:leading-10">
							Via Fellows pursue these goals through:
						</p>
						<ol className="mx-auto grid grid-cols-1  items-start justify-start gap-16 align-top text-lg leading-7 text-accent-dark md:grid-cols-3 md:text-2xl md:leading-10">
							<li className="flex flex-col items-center justify-center gap-4">
								<div className="flex flex-col items-start justify-start gap-2">
									<div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-slate-600">
										<StudyIcon className="h-12 w-12 text-gray-800" />
									</div>
									{/* <h3 className="font-title text-2xl tracking-wide">Study</h3> */}
								</div>
								<div className="text-center">
									Regular seminars exploring the literary and philosophical
									foundations of Western society, beginning with Jerusalem,
									Athens, and Rome.
								</div>
							</li>
							<li className="flex flex-col items-center justify-center gap-4">
								<div className="flex flex-col items-start justify-start gap-2">
									<div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-slate-600">
										<WorkIcon className="h-12 w-12 text-gray-800" />
									</div>
									{/* <h3 className="font-title text-2xl tracking-wide">Work</h3> */}
								</div>
								<div className="text-center">
									Apprenticeships with Catholic professionals who are dedicated
									to preparing Fellows for success in their respective career
									path of interest.
								</div>
							</li>
							<li className="flex flex-col items-center justify-center gap-4">
								<div className="flex flex-col items-start justify-start gap-2">
									<div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-slate-600">
										<PrayIcon className="h-12 w-12 text-gray-800" />
									</div>
									{/* <h3 className="font-title text-2xl tracking-wide">Prayer</h3> */}
								</div>
								<div className="text-center">
									Frequent time in silent prayer, spiritual reading, and the
									sacraments with the guidance of spiritual directors.
								</div>
							</li>
						</ol>
					</div>
				</div>
			</section>
			<section className="mx-auto w-full space-y-6 bg-section p-6 py-8 md:p-36 md:pt-20">
				{/* <h2 className="font-title text-3xl tracking-wider">
					If you are interested in...
				</h2> */}
				<Interest />
			</section>
			<section
				id="socrates"
				className="relative flex h-[300px] min-h-[300px] items-center justify-center overflow-hidden border-b-2 border-black md:h-[420px] md:min-h-[66vh]"
			>
				<ImageOverlay src="/img/via3.jpeg" position="46% 78%" opacity="0.55" />
				<div className="z-20 flex flex-col space-y-10 p-6 text-white md:p-0">
					<h3 className="font-title text-2xl md:text-4xl">
						“Let him that would move the world, first move himself.”
					</h3>
					<p className="px-4 text-lg md:pr-12 md:text-right md:text-2xl">
						- Socrates
					</p>
				</div>
			</section>
		</>
	)
}

export function ErrorBoundary() {
	const error = useRouteError()
	if (isRouteErrorResponse(error)) {
		return <div />
	}
	return <div />
}

export function Interest() {
	return (
		<div className="w-full sm:mx-auto ">
			<Tabs defaultValue="default" className="" unstyled>
				<Tabs.List
					className="mx-auto grid grid-cols-1  items-start justify-start gap-16 align-top text-lg leading-7 text-accent-dark md:grid-cols-3 md:text-2xl md:leading-10"
					// className="mx-auto flex flex-col items-center justify-center gap-4 md:flex-row md:items-start md:justify-start md:gap-20 lg:gap-28"
				>
					<Tabs.Tab
						value="applying"
						className="group flex w-full min-w-[225px] flex-col items-center gap-4"
					>
						<div className="flex flex-col items-center gap-4">
							<div className="tab-icon flex h-20 w-20 items-center justify-center rounded-full border-4 border-gray-800 group-hover:border-slate-600 group-hover:bg-slate-500">
								<AppWindowIcon className="h-12 w-12 text-gray-800" />
							</div>
							<div className="tab-text font-title text-2xl tracking-wide group-hover:font-medium">
								Apply to Via
							</div>
						</div>
						<p>Apply to be a part of Via's 2024-2025 cohort.</p>
					</Tabs.Tab>
					<Tabs.Tab
						value="supporting"
						className="group flex w-full min-w-[225px] flex-col items-center gap-4"
					>
						<div className="flex flex-col items-center gap-4">
							<div className="tab-icon flex h-20 w-20 items-center justify-center rounded-full border-4 border-gray-800 group-hover:border-slate-600 group-hover:bg-slate-500">
								<ShieldIcon className="1ext-gray-800 h-12 w-32" />
							</div>
							<div className="tab-text font-title text-2xl tracking-wide group-hover:font-medium">
								Support Via
							</div>
						</div>
						<p>Support Via through prayer or a donation or both.</p>
					</Tabs.Tab>

					<Tabs.Tab
						value="learning"
						className="group flex w-full min-w-[225px] flex-col items-center gap-4"
					>
						<div className="flex flex-col items-center gap-4">
							<div className="tab-icon flex h-20 w-20 items-center justify-center rounded-full border-4 border-gray-800 group-hover:border-slate-600 group-hover:bg-slate-500">
								<InfoIcon className="1ext-gray-800 h-12 w-32" />
							</div>
							<div className="tab-text font-title text-2xl tracking-wide group-hover:font-medium">
								Learn more about Via
							</div>
						</div>
						<p>Get more information about Via.</p>
					</Tabs.Tab>
				</Tabs.List>
				<div className="mx-auto flex w-full justify-center px-4 pt-8 md:px-0">
					<Tabs.Panel value="default">&nbsp;</Tabs.Panel>
					<Tabs.Panel value="applying" className="w-[420px] px-4 md:px-0">
						<h2 className="font-title text-3xl tracking-wider">
							Request an application.
						</h2>
						<ApplyForm />
					</Tabs.Panel>
					<Tabs.Panel value="supporting" className="w-[420px] px-4 md:px-0">
						<h2 className="font-title text-3xl tracking-wider">
							Support Via with prayer, a donation, or both.
						</h2>
						<SupportForm />
					</Tabs.Panel>
					<Tabs.Panel value="learning" className="w-[420px] px-4 md:px-0">
						<h2 className="font-title text-3xl tracking-wider">
							Let us know your questions.
						</h2>
						<InquiryForm />
					</Tabs.Panel>
				</div>
			</Tabs>
		</div>
	)
}
